import { useCallback } from 'react';
import { Message } from '../types/chat.types';
import { exportChatToPdf, cleanMarkdown } from '../utils/chatExport';

const formatAsPlainText = (messages: Message[]) =>
  messages
    .map((message) => {
      const sender = message.role === 'user' ? 'User' : 'Assistant';
      const cleanContent = cleanMarkdown(message.content);
      return `${sender}:\n${cleanContent}`;
    })
    .join('\n\n---\n\n');

const buildFileName = (extension: string) =>
  `chat-phyto-${new Date().toISOString().slice(0, 10)}.${extension}`;

export const useChatExport = (messages: Message[]) => {
  const handleExportTxt = useCallback(() => {
    if (messages.length === 0 || typeof window === 'undefined') {
      return;
    }

    const txtContent = formatAsPlainText(messages);
    const blob = new Blob([txtContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);

    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = buildFileName('txt');
    window.document.body.appendChild(anchor);
    anchor.click();
    window.document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }, [messages]);

  const handleExportPdf = useCallback(async () => {
    if (messages.length === 0) {
      return;
    }
    await exportChatToPdf(messages);
  }, [messages]);

  const handleCopyToClipboard = useCallback(() => {
    if (messages.length === 0 || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }

    const txtContent = formatAsPlainText(messages);
    void navigator.clipboard.writeText(txtContent);
  }, [messages]);

  return {
    handleExportTxt,
    handleExportPdf,
    handleCopyToClipboard,
  };
};
